import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import SkipToContent from "@/components/a11y/SkipToContent";
import { ErrorBoundary } from "@/design-system/components/ErrorBoundary";
import { AuthProvider } from "@/auth/AuthContext";
import { ProtectedRoute, PublicOnlyRoute } from "@/auth/ProtectedRoute";
import { FeatureFlagsProvider } from "@/feature-flags/FeatureFlagsContext";
import { AdminPermissionsProvider } from "@/admin/PermissionsContext";
import Landing from "@/pages/Landing";
import LandingV2 from "@/pages/LandingV2";
import LandingRoute from "@/pages/LandingRoute";
// Wave 1 (Executive Foundation) — canonical 404 page.
import NotFound from "@/pages/NotFound";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Profile from "@/pages/Profile";
import Dashboard from "@/pages/Dashboard";
import Readiness from "@/pages/Readiness";
// Phase F2.2 — dedicated Credit Improvement route (`/credit-improvement`).
import CreditImprovement from "@/pages/CreditImprovement";
import Roadmap from "@/pages/Roadmap";
// Phase F2 tester feedback — `/credit-report` list-view now redirects to
// Credit Improvement; the standalone `CreditReport` page component is no
// longer wired. Kept as a source-only artifact for potential future
// re-enablement; no App-level import is needed.
import DisputeLetterWizard from "@/pages/DisputeLetterWizard";
import DisputeLetters from "@/pages/DisputeLetters";
import DisputeLetterDetail from "@/pages/DisputeLetterDetail";
import Partners from "@/pages/Partners";
import Comparison from "@/pages/Comparison";
import AvailableSoon from "@/pages/AvailableSoon";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Pricing from "@/pages/Pricing";
import SettingsBilling from "@/pages/SettingsBilling";
import StubCheckout from "@/pages/StubCheckout";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import AccountBilling from "@/pages/AccountBilling";
import AdminRoute from "@/admin/AdminRoute";
import AdminOverview from "@/admin/pages/Overview";
import AdminUsers from "@/admin/pages/Users";
// Phase G5 CP3 — Professional Marketplace admin console (tabs +
// verification queue + consultation inbox + categories editor).
import AdminMarketplacePage from "@/admin/pages/Marketplace";
import AdminMarketplaceProfessionalDetail from "@/admin/pages/marketplace/AdminProfessionalDetail";
import AdminFeatureFlags from "@/admin/pages/FeatureFlags";
import AdminTeam from "@/admin/pages/Team";
import AdminPartners from "@/admin/pages/Partners";
import AdminLetters from "@/admin/pages/Letters";
import AdminCreditReports from "@/admin/pages/CreditReports";
import AdminSiteSettings from "@/admin/pages/SiteSettings";
import AdminScoringRubric from "@/admin/pages/ScoringRubric";
import AdminAILogs from "@/admin/pages/AILogs";
// V2.1 Track 1 — Credit Engine Observability admin dashboard (hidden route,
// not surfaced in AdminLayout nav per V2 UI freeze at v2.0.0).
import AdminCreditEngineEvents from "@/admin/pages/CreditEngineEvents";
// V2.1 Phase 2 — Shadow report + Review queue (both hidden routes).
import AdminCreditEngineShadowReport from "@/admin/pages/CreditEngineShadowReport";
import AdminCreditEngineReviewQueue from "@/admin/pages/CreditEngineReviewQueue";
// V2.1 Phase 5A — AI Credit Analyst preview (hidden route).
import AdminCreditEngineAnalystPreview from "@/admin/pages/CreditEngineAnalystPreview";
// V2.1 Phase 5B — Letter drafts preview (hidden route) + user-facing dispute UI.
import AdminCreditEngineLetterDrafts from "@/admin/pages/CreditEngineLetterDrafts";
// V2.1 Phase 5C — Corpus health dashboard (hidden route).
import AdminCreditEngineCorpusHealth from "@/admin/pages/CreditEngineCorpusHealth";
// V2.1 Phase 5.5 Workstream 1 — Confidence gate diagnostics (hidden route).
import AdminCreditEngineConfidenceGate from "@/admin/pages/CreditEngineConfidenceGate";
// V2.1 Phase 5.5 Workstream 2 — Credit accuracy dashboard (hidden route).
import AdminCreditEngineAccuracy from "@/admin/pages/CreditEngineAccuracy";
import DisputeCandidates from "@/pages/DisputeCandidates";
import LetterPreview from "@/pages/LetterPreview";
import AdminAuditLogs from "@/admin/pages/AuditLogs";
import AdminEmailLogs from "@/admin/pages/EmailLogs";
import AdminEmailTemplates from "@/admin/pages/EmailTemplates";
import AdminBankStatements from "@/admin/pages/BankStatements";
// Informed OS Academy — Phase 1b learner surface. Admin studio ships
// in a later phase; only the learner routes are exposed here.
import AcademyLanding from "@/pages/academy/AcademyLanding";
import ProgramDetail from "@/pages/academy/ProgramDetail";
import MyLearning from "@/pages/academy/MyLearning";
import LessonPlayer from "@/pages/academy/LessonPlayer";
import AssessmentTake from "@/pages/academy/AssessmentTake";
import MyCertificates from "@/pages/academy/MyCertificates";
import CertificateVerify from "@/pages/academy/CertificateVerify";
import AdminAssessmentsList from "@/admin/pages/academy/AdminAssessmentsList";
import AdminAssessmentEditor from "@/admin/pages/academy/AdminAssessmentEditor";
import AdminCertificates from "@/admin/pages/academy/AdminCertificates";
import AdminCertificateConfig from "@/admin/pages/academy/AdminCertificateConfig";
import AdminAskInformed from "@/admin/pages/academy/AdminAskInformed";
import AcademyAskPage from "@/pages/academy/AcademyAskPage";
import AdminMailedLetters from "@/admin/pages/MailedLetters";
// V2.0 Phase 4a — Mortgage Readiness + Personal Credit Catalog admin pages.
import AdminMortgageConfig from "@/admin/pages/MortgageConfig";
import AdminCreditCatalog from "@/admin/pages/CreditCatalog";
import MortgageIntake from "@/pages/MortgageIntake";
// V2.0 Phase 4b — Financial Opportunities dashboard.
import Opportunities from "@/pages/Opportunities";
import BankStatements from "@/pages/BankStatements";
import MailedLetters from "@/pages/MailedLetters";
// Phase G5 CP2 — Professional Marketplace surface (listing + detail).
import MarketplaceRoute from "@/pages/MarketplaceRoute";
import ProfessionalDetailRoute from "@/pages/ProfessionalDetailRoute";
import AdminPlans from "@/admin/pages/Plans";
import AdminBilling from "@/admin/pages/Billing";
import AdminSystemConfig from "@/admin/pages/SystemConfig";
// V2 Batch C2 — new configuration surfaces.
import AdminForecastConfig from "@/admin/pages/ForecastConfig";
import AdminCreditTierRates from "@/admin/pages/CreditTierRates";
import AdminStub from "@/admin/pages/Stub";
// V1.1-Beta — beta launch kit admin pages + global widgets.
import AdminBetaCodes from "@/admin/pages/BetaCodes";
import AdminBetaFeedback from "@/admin/pages/BetaFeedback";
import AdminAnalyticsFunnel from "@/admin/pages/AnalyticsFunnel";
import AdminCoupons from "@/admin/pages/Coupons";
import AdminCouponsAnalytics from "@/admin/pages/CouponsAnalytics";
import AdminChurn from "@/admin/pages/Churn";
import AdminRevenue from "@/admin/pages/Revenue";
// Sprint 1 (Compliance Focus) — consolidated retention + consent + deletions
// visibility for ops.
import AdminCompliance from "@/admin/pages/Compliance";
import BetaFeedbackButton from "@/components/BetaFeedbackButton";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import EmailPreferences from "@/pages/EmailPreferences";
// V2.0 Phase 0 — Foundation (theme provider + admin-only preview route).
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import V2Preview from "@/pages/V2Preview";
// V2.0 Phase 5 Cluster B iter3 — placeholder legal pages, publicly accessible.
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
// V1.1-Beta — public beta landing + admin waitlist page.
import BetaLanding from "@/pages/BetaLanding";
import AdminBetaWaitlist from "@/admin/pages/BetaWaitlist";
// Phase G1 — Grant Navigator foundation. All user-facing grant routes
// are wrapped in `GrantFeatureRoute` so a global `grant_navigator_v1`
// toggle instantly cordons off the module without unmounting existing
// data. Backend routers additionally emit 403 `feature_disabled` for
// defense-in-depth; the frontend gate simply prevents the network round
// trip and shows a friendly explanation.
import GrantFinder from "@/pages/GrantFinder";
import GrantDetail from "@/pages/GrantDetail";
import SavedGrants from "@/pages/SavedGrants";
import GrantProfile from "@/pages/GrantProfile";
import GrantReadiness from "@/pages/GrantReadiness";
import GrantMatches from "@/pages/GrantMatches";
import GrantPipeline from "@/pages/GrantPipeline";
import AdminGrants from "@/admin/pages/Grants";
// Phase G3A — Business Document Vault™ pages (feature-flag gated).
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
// Phase G3C-A — Calendar (feature-flag gated on `calendar_v1`).
import Calendar from "@/pages/Calendar";
// Phase G3C-B — Notifications (feature-flag gated on `notifications_v1`).
import Notifications from "@/pages/Notifications";
// Phase G4A — Grant Writer (proposals). Premium-only + feature-flag
// gated on `grant_writer_v1`. Free-tier gets the inline upgrade CTA
// on /proposals via PremiumUpgradePanel scope="proposals".
import Proposals from "@/pages/Proposals";
import ProposalNew from "@/pages/ProposalNew";
import ProposalWizard from "@/pages/ProposalWizard";
import ProposalDraft from "@/pages/ProposalDraft";
import FeatureDisabled from "@/feature-flags/FeatureDisabled";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";

function ThemedToaster() {
  const { effectiveTheme } = useTheme();
  return (
    <Toaster
      theme={effectiveTheme === "dark" ? "dark" : "light"}
      position="bottom-right"
      richColors
    />
  );
}

/**
 * FeatureFlagGate — shared shell used by every module-specific route
 * gate below. Waits for the `/api/feature-flags` call to resolve
 * (`loaded === true`) before making the enabled/disabled decision.
 * Before load resolves, renders nothing (the AppNav shell frames
 * the empty page in the surrounding layout).
 *
 * Introduced during the G4B CP2 rerun P0 fix — the previous
 * per-module gates rendered `FeatureDisabled` on first paint when
 * the client-side `DEFAULT_FLAGS` mirror was missing a key (e.g.
 * `grant_writer_v1`). That produced a flash-of-disabled shell that
 * automation captured as a persistent misroute. Gating on `loaded`
 * eliminates the race for every module simultaneously.
 */
function FeatureFlagGate({ flagKey, testId, title, message, children }) {
  const { isEnabled, loaded } = useFeatureFlags();
  if (!loaded) return null;
  if (!isEnabled(flagKey)) {
    return (
      <FeatureDisabled
        testId={testId}
        title={title}
        message={message}
      />
    );
  }
  return children;
}

/**
 * Phase G1 — Grant Navigator feature gate. Renders the underlying page
 * when `grant_navigator_v1` is on; otherwise renders the shared
 * FeatureDisabled empty state without mounting the child page (no API
 * calls, no useEffect fetches).
 */
function GrantFeatureRoute({ children }) {
  return (
    <FeatureFlagGate
      flagKey="grant_navigator_v1"
      testId="grant-navigator-feature-disabled"
      title="Grant Navigator is temporarily disabled"
      message="An administrator has paused the Grant Navigator. Your saved grants and profile data remain in the system and will re-appear when the feature is re-enabled."
    >
      {children}
    </FeatureFlagGate>
  );
}

/**
 * Phase G3A — Business Document Vault feature gate. Same pattern as
 * `GrantFeatureRoute`: renders the underlying page when
 * `business_document_vault_v1` is on; otherwise renders the shared
 * FeatureDisabled empty state without mounting the child page.
 */
function DocumentsFeatureRoute({ children }) {
  return (
    <FeatureFlagGate
      flagKey="business_document_vault_v1"
      testId="business-document-vault-feature-disabled"
      title="Business Document Vault is temporarily disabled"
      message="An administrator has paused the Business Document Vault. Your uploaded documents remain in the system and will re-appear when the feature is re-enabled."
    >
      {children}
    </FeatureFlagGate>
  );
}

/**
 * Phase G3C-A — Calendar feature gate. Renders the calendar page when
 * `calendar_v1` is on; otherwise renders the shared FeatureDisabled
 * shell without mounting the calendar surface (no API calls, no
 * useEffect fetches).
 */
function CalendarFeatureRoute({ children }) {
  return (
    <FeatureFlagGate
      flagKey="calendar_v1"
      testId="calendar-feature-disabled"
      title="Calendar is temporarily disabled"
      message="An administrator has paused the Calendar module. Your saved grant deadlines and reminders remain in the system and will re-appear when the feature is re-enabled."
    >
      {children}
    </FeatureFlagGate>
  );
}

/**
 * Phase G3C-B — Notifications feature gate. Same pattern as calendar
 * — renders `/notifications` when `notifications_v1` is on; otherwise
 * the shared FeatureDisabled shell. The `AppNav` bell also hides
 * itself independently via the same flag context.
 */
function NotificationsFeatureRoute({ children }) {
  return (
    <FeatureFlagGate
      flagKey="notifications_v1"
      testId="notifications-feature-disabled"
      title="Notifications are temporarily disabled"
      message="An administrator has paused the Notifications module. Your history remains in the system and will re-appear when the feature is re-enabled."
    >
      {children}
    </FeatureFlagGate>
  );
}

/**
 * Phase G4A — Grant Writer feature gate. Renders the Proposal Library
 * and wizard when `grant_writer_v1` is on; otherwise renders the
 * shared FeatureDisabled shell without mounting the child pages
 * (no API calls, no useEffect fetches). Premium-tier gating happens
 * SEPARATELY on the backend router (HTTP 402); the frontend surface
 * is available to every authenticated user and switches to the
 * `PremiumUpgradePanel scope="proposals"` inline when the 402 fires.
 */
function ProposalsFeatureRoute({ children }) {
  return (
    <FeatureFlagGate
      flagKey="grant_writer_v1"
      testId="grant-writer-feature-disabled"
      title="Grant Writer is temporarily disabled"
      message="An administrator has paused the Grant Writer module. Your saved proposal drafts remain in the system and will re-appear when the feature is re-enabled."
    >
      {children}
    </FeatureFlagGate>
  );
}

function App() {
  return (
    <div className="App">
      <FeatureFlagsProvider>
        <AuthProvider>
          <AdminPermissionsProvider>
            <ThemeProvider>
            <BrowserRouter>
              <ErrorBoundary>
              {/* Phase 6 a11y — Global skip-to-content link. Rendered so
                  it lands FIRST in the tab order on every route,
                  satisfying WCAG 2.4.1 (Bypass Blocks). The target
                  `#main-content` is provided by each layout shell
                  (AuthShell / AppNav / AdminLayout / GrantShell / LandingV2).

                  Wave 2 (Signature Authenticated Routes): the previous
                  global amber email-verification banner has been
                  removed. Email-verification status now surfaces as a
                  small non-intrusive affordance inside `/profile`
                  (see `EmailVerificationChip`). This restores the
                  enterprise-financial visual tone by eliminating the
                  pastel warm-banner that sat above every authenticated
                  page. */}
              <SkipToContent />
              <ThemedToaster />
              <AnalyticsTracker />
              <Routes>
                <Route path="/" element={<LandingRoute />} />
                {/* Always-reachable direct-compare URLs — the flag only
                    controls which one renders at the bare `/` root.
                    Ops can hit these paths to visually compare either
                    landing regardless of the `landing_v2_enabled`
                    setting. */}
                <Route path="/landing-v1" element={<Landing />} />
                <Route path="/landing-v2" element={<LandingV2 />} />
                {/* Phase N3.2 — legacy nav consolidator.
                    `/settings/billing` was surfaced by the prior nav
                    but had zero inbound `<Link>` references. Redirect
                    to the canonical `/account/billing` so bookmarks
                    and email deep-links keep resolving. `replace: true`
                    so the back-button doesn't create a loop. */}
                <Route path="/settings/billing" element={<Navigate to="/account/billing" replace />} />
                {/* Phase 5 hygiene — tester agents (and some external
                    docs) reach for `/credit-reports/comparison` because
                    the API prefix is `/api/credit-reports/*`. Redirect
                    to the canonical single-word `/comparison` route so
                    those inbound links resolve instead of rendering a
                    blank shell. `replace: true` keeps the back-button
                    honest. */}
                <Route path="/credit-reports/comparison" element={<Navigate to="/comparison" replace />} />
                {/* V1.1-Beta — public beta landing page (unauthenticated). */}
                <Route path="/beta" element={<BetaLanding />} />
                {/* V2.0 Phase 5 Cluster B iter3 — placeholder legal pages.
                    Publicly accessible (no auth gate). `/legal/*` are the
                    canonical URLs (V2.1 Phase 5.5 P2 item #7); the bare
                    `/privacy` and `/terms` paths issue a client-side
                    replace redirect to the canonical taxonomy. This is
                    the SPA equivalent of an HTTP 301: the URL in the
                    address bar becomes `/legal/privacy` without adding a
                    history entry, and any inbound link is normalised.
                */}
                <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/legal/terms" element={<TermsOfService />} />
                <Route
                  path="/signup"
                  element={
                    <PublicOnlyRoute>
                      <Signup />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <PublicOnlyRoute>
                      <Login />
                    </PublicOnlyRoute>
                  }
                />
                <Route path="/accept-invite/:token" element={<AcceptInvite />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing/cancel" element={<BillingCancel />} />
                <Route
                  path="/account/billing"
                  element={
                    <ProtectedRoute>
                      <AccountBilling />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/billing"
                  element={
                    <ProtectedRoute>
                      <SettingsBilling />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing/stub-checkout"
                  element={
                    <ProtectedRoute>
                      <StubCheckout />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/readiness"
                  element={
                    <ProtectedRoute>
                      <Readiness />
                    </ProtectedRoute>
                  }
                />
                {/* Informed OS Academy — Phase 1b learner surface.
                    All three routes are auth-gated client-side; the
                    server enforces the `academy_enabled` feature flag
                    authoritatively (404 `feature_disabled` when off). */}
                <Route
                  path="/academy"
                  element={
                    <ProtectedRoute>
                      <AcademyLanding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/academy/programs/:slug"
                  element={
                    <ProtectedRoute>
                      <ProgramDetail />
                    </ProtectedRoute>
                  }
                />
                {/* Informed OS Academy — Phase 1c lesson player.
                    Nested by program slug + lesson slug for shareable
                    URLs. All progress + notes routes are enrollment-
                    gated server-side. */}
                <Route
                  path="/academy/programs/:programSlug/lessons/:lessonSlug"
                  element={
                    <ProtectedRoute>
                      <LessonPlayer />
                    </ProtectedRoute>
                  }
                />
                {/* Informed OS Academy — Phase 1d assessment take-flow.
                    Two shapes: nested-under-lesson quiz OR program-level final.
                    Answer-key stripping enforced server-side by the assessments
                    router's positive allow-list projection. */}
                <Route
                  path="/academy/programs/:programSlug/lessons/:lessonSlug/assessment/:assessmentSlug"
                  element={
                    <ProtectedRoute>
                      <AssessmentTake />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/academy/programs/:programSlug/final/:assessmentSlug"
                  element={
                    <ProtectedRoute>
                      <AssessmentTake />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/academy/my-learning"
                  element={
                    <ProtectedRoute>
                      <MyLearning />
                    </ProtectedRoute>
                  }
                />
                {/* Informed OS Academy — Phase 1e certificates */}
                <Route
                  path="/academy/certificates"
                  element={
                    <ProtectedRoute>
                      <MyCertificates />
                    </ProtectedRoute>
                  }
                />
                {/* Public verification page — NO auth wrapper. */}
                <Route path="/academy/verify/:code" element={<CertificateVerify />} />
                {/* Owner-only Ask Informed preview page. The page itself
                    redirects non-owners; the server also enforces the
                    `team_role == 'owner'` bypass on every underlying
                    Ask endpoint so this is a defense-in-depth split. */}
                <Route
                  path="/academy/ask"
                  element={
                    <ProtectedRoute>
                      <AcademyAskPage />
                    </ProtectedRoute>
                  }
                />
                {/*
                 * Phase F2 tester feedback — `/credit-report` and
                 * `/credit-report/:id` are legacy landings that predate
                 * the Credit Readiness (`/readiness`) + Credit Improvement
                 * (`/credit-improvement`) route split. The list-view
                 * `/credit-report` now redirects to Credit Improvement
                 * (which already surfaces the F1 IdentityIQ empty state).
                 * The detail-view `/credit-report/:id` is preserved so
                 * deep-linked bookmarks to specific historical reports
                 * still resolve.
                 */}
                <Route
                  path="/credit-report"
                  element={<Navigate to="/credit-improvement" replace />}
                />
                <Route
                  path="/credit-improvement"
                  element={
                    <ProtectedRoute>
                      <CreditImprovement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dispute-letters/new"
                  element={
                    <ProtectedRoute>
                      <DisputeLetterWizard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dispute-letters/:id"
                  element={
                    <ProtectedRoute>
                      <DisputeLetterDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dispute-letters"
                  element={
                    <ProtectedRoute>
                      <DisputeLetters />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roadmap"
                  element={
                    <ProtectedRoute>
                      <Roadmap />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/partners"
                  element={
                    <ProtectedRoute>
                      <Partners />
                    </ProtectedRoute>
                  }
                />
                {/* Phase G1 — Grant Navigator user routes. Order matches
                    moduleNavConfig.js (partners → grants → disputes). Each
                    surface is auth-gated then flag-gated. `/grants/saved`
                    must come before `/grants/:id` so the string literal
                    wins the route match. */}
                <Route
                  path="/grants"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantFinder />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/grants/saved"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <SavedGrants />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G2A CP3 — Top Matches. Backend enforces
                    Premium-only via 402; free tier sees the inline
                    upgrade CTA rendered by GrantMatches itself. */}
                <Route
                  path="/grants/matches"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantMatches />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G2B CP2 — Pipeline Kanban. Same Premium-402
                    inline-upgrade pattern as /grants/matches. */}
                <Route
                  path="/grants/pipeline"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantPipeline />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/grants/:id"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantDetail />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/grant-profile"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantProfile />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/grant-readiness"
                  element={
                    <ProtectedRoute>
                      <GrantFeatureRoute>
                        <GrantReadiness />
                      </GrantFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G3A — Business Document Vault™. Feature-flag
                    gated on `business_document_vault_v1`. */}
                <Route
                  path="/documents"
                  element={
                    <ProtectedRoute>
                      <DocumentsFeatureRoute>
                        <Documents />
                      </DocumentsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents/:id"
                  element={
                    <ProtectedRoute>
                      <DocumentsFeatureRoute>
                        <DocumentDetail />
                      </DocumentsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G3C-A — Calendar. Feature-flag gated on
                    `calendar_v1`. Backend router also enforces the
                    same flag (404 `feature_disabled`) for defense-
                    in-depth. */}
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <CalendarFeatureRoute>
                        <Calendar />
                      </CalendarFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G3C-B — Notifications full-history route.
                    Feature-flag gated on `notifications_v1`. Backend
                    enforces the same flag (404 `feature_disabled`)
                    for defense-in-depth. */}
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsFeatureRoute>
                        <Notifications />
                      </NotificationsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G4A — Grant Writer (proposal wizard + CRUD).
                    Backend Premium-gates via HTTP 402; frontend surface
                    is auth-gated + feature-flag-gated so Free users can
                    still see the /proposals route and land on the
                    inline upgrade CTA. `/proposals/new` and
                    `/proposals/{id}/edit` share the same gating. */}
                <Route
                  path="/proposals"
                  element={
                    <ProtectedRoute>
                      <ProposalsFeatureRoute>
                        <Proposals />
                      </ProposalsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals/new"
                  element={
                    <ProtectedRoute>
                      <ProposalsFeatureRoute>
                        <ProposalNew />
                      </ProposalsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals/:id/edit"
                  element={
                    <ProtectedRoute>
                      <ProposalsFeatureRoute>
                        <ProposalWizard />
                      </ProposalsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Phase G4B — rendered AI-assisted proposal draft.
                    Backend generates via POST /proposals/{id}/generate;
                    frontend reads all 11 sections + per-section
                    regenerate + inline edit. */}
                <Route
                  path="/proposals/:id/draft"
                  element={
                    <ProtectedRoute>
                      <ProposalsFeatureRoute>
                        <ProposalDraft />
                      </ProposalsFeatureRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/comparison"
                  element={
                    <ProtectedRoute>
                      <Comparison />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mortgage-intake"
                  element={
                    <ProtectedRoute>
                      <MortgageIntake />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/opportunities"
                  element={
                    <ProtectedRoute>
                      <Opportunities />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/bank-statements"
                  element={
                    <ProtectedRoute>
                      <BankStatements />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mailed-letters"
                  element={
                    <ProtectedRoute>
                      <MailedLetters />
                    </ProtectedRoute>
                  }
                />
                {/* Phase G5 CP2 — Professional Marketplace surface. Auth-only.
                    Listing lives at /marketplace, detail at /marketplace/:id.
                    Demo profiles show a disclosure block ABOVE any CTA;
                    real professionals get the Request Consultation modal. */}
                <Route
                  path="/marketplace"
                  element={
                    <ProtectedRoute>
                      <MarketplaceRoute />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/:professionalId"
                  element={
                    <ProtectedRoute>
                      <ProfessionalDetailRoute />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin" element={<AdminRoute><AdminOverview /></AdminRoute>} />
                {/* Informed OS Academy — Phase 1d Admin Studio */}
                <Route path="/admin/academy/assessments" element={<AdminRoute><AdminAssessmentsList /></AdminRoute>} />
                <Route path="/admin/academy/assessments/:id" element={<AdminRoute><AdminAssessmentEditor /></AdminRoute>} />
                {/* Informed OS Academy — Phase 1e Admin Certificates */}
                <Route path="/admin/academy/certificates" element={<AdminRoute><AdminCertificates /></AdminRoute>} />
                <Route path="/admin/academy/programs/:programId/certificate-config" element={<AdminRoute><AdminCertificateConfig /></AdminRoute>} />
                {/* Informed OS Academy — Phase 1f Ask Informed Studio */}
                <Route path="/admin/academy/ask" element={<AdminRoute><AdminAskInformed /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                {/* Phase G5 CP3 — Marketplace admin console. */}
                <Route path="/admin/marketplace" element={<AdminRoute><AdminMarketplacePage /></AdminRoute>} />
                <Route path="/admin/marketplace/professionals/:professionalId" element={<AdminRoute><AdminMarketplaceProfessionalDetail /></AdminRoute>} />
                <Route path="/admin/team" element={<AdminRoute><AdminTeam /></AdminRoute>} />
                <Route path="/admin/feature-flags" element={<AdminRoute><AdminFeatureFlags /></AdminRoute>} />
                <Route path="/admin/partners" element={<AdminRoute><AdminPartners /></AdminRoute>} />
                {/* Phase G1 — Grant Navigator admin CRUD. Same permission
                    tier as Credit Catalog / Mortgage Config (edit_scoring_rubric)
                    per Layer-6 dispatch. Not flag-gated on the frontend:
                    the ability to enable/disable the module lives on the
                    same admin surface, so operators can toggle without
                    losing access to the catalog. */}
                <Route path="/admin/grants" element={<AdminRoute><AdminGrants /></AdminRoute>} />
                <Route path="/admin/letters" element={<AdminRoute><AdminLetters /></AdminRoute>} />
                <Route path="/admin/credit-reports" element={<AdminRoute><AdminCreditReports /></AdminRoute>} />
                <Route path="/admin/site-settings" element={<AdminRoute><AdminSiteSettings /></AdminRoute>} />
                <Route path="/admin/compliance" element={<AdminRoute><AdminCompliance /></AdminRoute>} />
                <Route path="/admin/scoring-rubric" element={<AdminRoute><AdminScoringRubric /></AdminRoute>} />
                <Route path="/admin/ai-logs" element={<AdminRoute><AdminAILogs /></AdminRoute>} />
                {/* V2.1 Track 1 — Credit Engine Observability (Phase 0.5). Hidden route
                    reachable only via direct URL; NOT added to AdminLayout nav. */}
                <Route path="/admin/credit-engine/events" element={<AdminRoute><AdminCreditEngineEvents /></AdminRoute>} />
                <Route path="/admin/credit-engine/shadow-report" element={<AdminRoute><AdminCreditEngineShadowReport /></AdminRoute>} />
                <Route path="/admin/credit-engine/review-queue" element={<AdminRoute><AdminCreditEngineReviewQueue /></AdminRoute>} />
                <Route path="/admin/credit-engine/analyst-preview" element={<AdminRoute><AdminCreditEngineAnalystPreview /></AdminRoute>} />
                <Route path="/admin/credit-engine/letter-drafts" element={<AdminRoute><AdminCreditEngineLetterDrafts /></AdminRoute>} />
                <Route path="/admin/credit-engine/corpus-health" element={<AdminRoute><AdminCreditEngineCorpusHealth /></AdminRoute>} />
                <Route path="/admin/credit-engine/confidence-gate" element={<AdminRoute><AdminCreditEngineConfidenceGate /></AdminRoute>} />
                <Route path="/admin/credit-engine/accuracy" element={<AdminRoute><AdminCreditEngineAccuracy /></AdminRoute>} />
                {/* V2.1 Phase 5B — user-facing dispute candidates + letter preview. Net-new routes; V2.0 frozen paths untouched. */}
                <Route path="/credit/disputes/:reportId" element={<ProtectedRoute><DisputeCandidates /></ProtectedRoute>} />
                <Route path="/credit/letters/:draftId" element={<ProtectedRoute><LetterPreview /></ProtectedRoute>} />
                <Route path="/admin/audit-logs" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
                <Route path="/admin/email-logs" element={<AdminRoute><AdminEmailLogs /></AdminRoute>} />
                <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
                <Route path="/admin/bank-statements" element={<AdminRoute><AdminBankStatements /></AdminRoute>} />
                <Route path="/admin/plans" element={<AdminRoute><AdminPlans /></AdminRoute>} />
                <Route path="/admin/billing" element={<AdminRoute><AdminBilling /></AdminRoute>} />
                <Route path="/admin/system-config" element={<AdminRoute><AdminSystemConfig /></AdminRoute>} />
                <Route path="/admin/mailed-letters" element={<AdminRoute><AdminMailedLetters /></AdminRoute>} />
                {/* V2.0 Phase 4a — Mortgage config + credit catalog */}
                <Route path="/admin/mortgage-config" element={<AdminRoute><AdminMortgageConfig /></AdminRoute>} />
                <Route path="/admin/credit-catalog" element={<AdminRoute><AdminCreditCatalog /></AdminRoute>} />
                {/* V2 Batch C2 — dedicated forecast constants + tier rates admin surfaces. */}
                <Route path="/admin/forecast-config" element={<AdminRoute><AdminForecastConfig /></AdminRoute>} />
                <Route path="/admin/credit-tier-rates" element={<AdminRoute><AdminCreditTierRates /></AdminRoute>} />
                {/* V1.1-Beta — beta launch kit */}
                <Route path="/admin/beta-codes" element={<AdminRoute><AdminBetaCodes /></AdminRoute>} />
                <Route path="/admin/beta-feedback" element={<AdminRoute><AdminBetaFeedback /></AdminRoute>} />
                <Route path="/admin/analytics/funnel" element={<AdminRoute><AdminAnalyticsFunnel /></AdminRoute>} />
                <Route path="/admin/coupons" element={<AdminRoute><AdminCoupons /></AdminRoute>} />
                <Route path="/admin/coupons/analytics" element={<AdminRoute><AdminCouponsAnalytics /></AdminRoute>} />
                <Route path="/admin/churn" element={<AdminRoute><AdminChurn /></AdminRoute>} />
                <Route path="/admin/revenue" element={<AdminRoute><AdminRevenue /></AdminRoute>} />
                {/* V1.1-Beta — public /beta waitlist admin view. */}
                <Route path="/admin/beta-waitlist" element={<AdminRoute><AdminBetaWaitlist /></AdminRoute>} />
                <Route path="/settings/email-preferences" element={<ProtectedRoute><EmailPreferences /></ProtectedRoute>} />
                <Route path="/v2-preview" element={<AdminRoute><V2Preview /></AdminRoute>} />
                {/* Wave 1 (Executive Foundation) — 404 catch-all. Must
                    stay last inside <Routes>. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              {/* Phase 6 a11y — rendered AFTER <Routes> so it lands
                  last in the DOM tab order. This keeps the SkipToContent
                  link inside each layout as the FIRST focusable element
                  on any page (WCAG 2.4.1). The floating pill is still
                  keyboard-reachable — it's just last in order. */}
              <BetaFeedbackButton />
              </ErrorBoundary>
            </BrowserRouter>
            </ThemeProvider>
          </AdminPermissionsProvider>
        </AuthProvider>
      </FeatureFlagsProvider>
    </div>
  );
}

export default App;
